import React, { useState, useEffect } from 'react';
import { User, Bell, Lock, Globe, Moon, Sun, Mail, Shield, Phone, Award, Zap, Database } from 'lucide-react';
import GlassCard from '../../components/ui/GlassCard';
import { learningAPI, studentAPI } from '../../services/api';
import { uploadFile } from '../../services/storageService';

const Settings = () => {
    const [activeTab, setActiveTab] = useState('profile');
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [recentActivity, setRecentActivity] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });

    // Form states
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        mobileNo: '',
        bio: 'English language learner'
    });

    useEffect(() => {
        const userData = JSON.parse(localStorage.getItem('user') || 'null');
        setUser(userData);
        if (userData) {
            setFormData({
                fullName: userData.full_name || '',
                email: userData.email || '',
                mobileNo: userData.mobile_no || '',
                bio: userData.bio || 'English language learner'
            });
        }
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [profileRes, activityRes] = await Promise.all([
                learningAPI.getProfile(),
                learningAPI.getRecentActivity(5)
            ]);
            
            // Use the flattened properties from the payload
            setProfile(profileRes.data);
            setRecentActivity(activityRes.data || []);
            
            // Sync mobile number if it came from the profile response
            if (profileRes.data.mobile_no) {
                setFormData(prev => ({ ...prev, mobileNo: profileRes.data.mobile_no }));
            }
        } catch (error) {
            console.error('Error fetching settings data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAvatarUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        try {
            const path = `profile_pictures/user_${user.id || Date.now()}_${file.name}`;
            const downloadUrl = await uploadFile(file, path);
            
            const updatedUser = { ...user, avatar: downloadUrl };
            setUser(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));
            setMessage({ text: 'Profile picture updated successfully!', type: 'success' });
        } catch (error) {
            console.error("Upload failed", error);
            setMessage({ text: 'Failed to upload image.', type: 'error' });
        } finally {
            setUploading(false);
            setTimeout(() => setMessage({ text: '', type: '' }), 3000);
        }
    };

    const handleSaveProfile = async () => {
        setSaving(true);
        try {
            const response = await studentAPI.updateProfile({
                full_name: formData.fullName,
                mobile_no: formData.mobileNo
            });
            
            // Update local user object
            const updatedUser = { 
                ...user, 
                full_name: formData.fullName,
                mobile_no: formData.mobileNo 
            };
            setUser(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));
            
            setMessage({ text: 'Profile updated successfully!', type: 'success' });
            fetchData(); // Refresh profile data
        } catch (error) {
            setMessage({ text: 'Failed to update profile.', type: 'error' });
        } finally {
            setSaving(false);
            setTimeout(() => setMessage({ text: '', type: '' }), 3000);
        }
    };

    if (!user) return <div style={{ color: 'white', textAlign: 'center', marginTop: '100px' }}>Loading...</div>;

    return (
        <div style={styles.pageWrapper}>
            {/* Header / Banner Area */}
            <div style={styles.banner}>
                <div className="container" style={styles.bannerContent}>
                    <label style={styles.avatarLabel}>
                        <div style={{
                            ...styles.avatar,
                            background: user.avatar ? `url(${user.avatar}) center/cover` : 'var(--primary)',
                            opacity: uploading ? 0.5 : 1
                        }}>
                            {!user.avatar && (user.full_name ? user.full_name.charAt(0).toUpperCase() : 'U')}
                            {uploading && <div style={styles.uploadOverlay}>...</div>}
                        </div>
                        <input type="file" accept="image/*" onChange={handleAvatarUpload} style={{ display: 'none' }} disabled={uploading} />
                    </label>
                    <div style={styles.headerText}>
                        <h1 style={styles.userName}>{user.full_name}</h1>
                        <div style={styles.badgeRow}>
                            <span style={styles.roleBadge}>{user.role || 'Student'}</span>
                            {profile && (
                                <span style={styles.levelBadge}>Level {profile.level || 1}</span>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="container" style={styles.mainContainer}>
                {/* Stats Row */}
                <div style={styles.statsGrid}>
                    <GlassCard>
                        <div style={styles.statLabel}>Total XP</div>
                        <div className="text-gradient" style={styles.statValue}>
                            {loading ? '...' : (profile?.xp || 0).toLocaleString()}
                        </div>
                    </GlassCard>
                    <GlassCard>
                        <div style={styles.statLabel}>Day Streak</div>
                        <div style={{ ...styles.statValue, color: '#fbbf24' }}>
                            🔥 {loading ? '...' : (profile?.streak || 0)}
                        </div>
                    </GlassCard>
                    <GlassCard>
                        <div style={styles.statLabel}>ZenCoins</div>
                        <div style={{ ...styles.statValue, color: '#38bdf8' }}>
                            💎 {loading ? '...' : (profile?.zen_coins || 0).toLocaleString()}
                        </div>
                    </GlassCard>
                </div>

                <div style={styles.contentLayout}>
                    {/* Sidebar Tabs */}
                    <div style={styles.sidebar}>
                        <GlassCard style={{ padding: '10px' }}>
                            <button 
                                onClick={() => setActiveTab('profile')}
                                style={{ ...styles.tabButton, ...(activeTab === 'profile' ? styles.tabButtonActive : {}) }}
                            >
                                <User size={18} /> Profile
                            </button>
                            <button 
                                onClick={() => setActiveTab('notifications')}
                                style={{ ...styles.tabButton, ...(activeTab === 'notifications' ? styles.tabButtonActive : {}) }}
                            >
                                <Bell size={18} /> Notifications
                            </button>
                            <button 
                                onClick={() => setActiveTab('security')}
                                style={{ ...styles.tabButton, ...(activeTab === 'security' ? styles.tabButtonActive : {}) }}
                            >
                                <Lock size={18} /> Security
                            </button>
                            <button 
                                onClick={() => setActiveTab('activity')}
                                style={{ ...styles.tabButton, ...(activeTab === 'activity' ? styles.tabButtonActive : {}) }}
                            >
                                <Zap size={18} /> Activity
                            </button>
                        </GlassCard>
                    </div>

                    {/* Tab Content */}
                    <div style={styles.contentArea}>
                        {message.text && (
                            <div style={{
                                ...styles.alert,
                                backgroundColor: message.type === 'success' ? 'rgba(74, 222, 128, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                color: message.type === 'success' ? '#4ade80' : '#f87171',
                                border: `1px solid ${message.type === 'success' ? '#4ade80' : '#f87171'}`
                            }}>
                                {message.text}
                            </div>
                        )}

                        <GlassCard>
                            {activeTab === 'profile' && (
                                <div style={styles.formSection}>
                                    <h2 style={styles.sectionTitle}>Profile Settings</h2>
                                    <div style={styles.formGrid}>
                                        <div style={styles.inputGroup}>
                                            <label style={styles.label}>Full Name</label>
                                            <div style={styles.inputWrapper}>
                                                <User size={16} style={styles.inputIcon} />
                                                <input 
                                                    style={styles.input}
                                                    value={formData.fullName}
                                                    onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                                                />
                                            </div>
                                        </div>
                                        <div style={styles.inputGroup}>
                                            <label style={styles.label}>Email Address</label>
                                            <div style={styles.inputWrapper}>
                                                <Mail size={16} style={styles.inputIcon} />
                                                <input style={{...styles.input, opacity: 0.6}} value={formData.email} disabled />
                                            </div>
                                        </div>
                                        <div style={styles.inputGroup}>
                                            <label style={styles.label}>Mobile Number</label>
                                            <div style={styles.inputWrapper}>
                                                <Phone size={16} style={styles.inputIcon} />
                                                <input 
                                                    style={styles.input}
                                                    placeholder="Enter mobile number"
                                                    value={formData.mobileNo}
                                                    onChange={(e) => setFormData({...formData, mobileNo: e.target.value})}
                                                />
                                            </div>
                                        </div>
                                        <div style={styles.inputGroupFull}>
                                            <label style={styles.label}>Bio</label>
                                            <textarea 
                                                style={styles.textarea}
                                                value={formData.bio}
                                                onChange={(e) => setFormData({...formData, bio: e.target.value})}
                                                rows="3"
                                            />
                                        </div>
                                    </div>
                                    <button 
                                        style={styles.saveButton} 
                                        onClick={handleSaveProfile}
                                        disabled={saving}
                                    >
                                        {saving ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </div>
                            )}

                            {activeTab === 'notifications' && (
                                <div style={styles.formSection}>
                                    <h2 style={styles.sectionTitle}>Notification Preferences</h2>
                                    <p style={{ color: '#94a3b8', marginBottom: '20px' }}>Coming Soon: Granular controls for email and push notifications.</p>
                                </div>
                            )}

                            {activeTab === 'security' && (
                                <div style={styles.formSection}>
                                    <h2 style={styles.sectionTitle}>Security Settings</h2>
                                    <button style={styles.secondaryButton}>Change Password</button>
                                    <div style={{ marginTop: '40px', padding: '20px', background: 'rgba(239, 68, 68, 0.05)', borderRadius: '12px', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                                        <h3 style={{ color: '#f87171', marginBottom: '10px' }}>Danger Zone</h3>
                                        <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginBottom: '15px' }}>Deleting your account is permanent and cannot be undone.</p>
                                        <button style={styles.dangerButton}>Delete Account</button>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'activity' && (
                                <div style={styles.formSection}>
                                    <h2 style={styles.sectionTitle}>Recent Activity</h2>
                                    {recentActivity.length > 0 ? (
                                        recentActivity.map((act, index) => (
                                            <div key={act.id || index} style={{
                                                ...styles.activityItem,
                                                borderBottom: index < recentActivity.length - 1 ? '1px solid #e2e8f0' : 'none'
                                            }}>
                                                <div style={styles.activityInfo}>
                                                    <div style={styles.activityIcon}>📚</div>
                                                    <div>
                                                        <div style={{ color: '#1e293b', fontWeight: 600 }}>{act.lesson_title || 'Completed Lesson'}</div>
                                                        <div style={{ color: '#64748b', fontSize: '0.8rem' }}>
                                                            {new Date(act.completed_at).toLocaleDateString()} • {new Date(act.completed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div style={{ color: 'var(--primary)', fontWeight: 'bold' }}>+{act.xp_earned || 0} XP</div>
                                            </div>
                                        ))
                                    ) : (
                                        <div style={{ textAlign: 'center', color: '#64748b', padding: '40px 0' }}>
                                            No recent activity found. Start learning to see your progress!
                                        </div>
                                    )}
                                </div>
                            )}
                        </GlassCard>
                    </div>
                </div>
            </div>
        </div>
    );
};

const styles = {
    pageWrapper: {
        background: 'transparent',
        minHeight: '100vh',
        paddingBottom: '80px'
    },
    banner: {
        height: '250px',
        background: 'linear-gradient(135deg, var(--primary) 0%, #22c55e 100%)',
        display: 'flex',
        alignItems: 'end',
        paddingBottom: '30px',
        position: 'relative'
    },
    bannerContent: {
        display: 'flex',
        alignItems: 'center',
        gap: '24px'
    },
    avatarLabel: {
        cursor: 'pointer',
        position: 'relative'
    },
    avatar: {
        width: 'clamp(80px, 15vw, 120px)',
        height: 'clamp(80px, 15vw, 120px)',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 'clamp(2rem, 5vw, 3rem)',
        color: 'var(--primary)',
        backgroundColor: '#ffffff',
        border: '4px solid rgba(255,255,255,0.8)',
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
        transition: 'all 0.3s ease'
    },
    uploadOverlay: {
        position: 'absolute',
        fontSize: '1rem',
        background: 'rgba(255,255,255,0.7)',
        color: '#333',
        width: '100%',
        height: '100%',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    headerText: {
        flex: 1
    },
    userName: {
        color: 'white',
        fontSize: 'clamp(1.5rem, 4vw, 2.2rem)',
        marginBottom: '8px',
        fontWeight: '700',
        textShadow: '0 2px 4px rgba(0,0,0,0.1)'
    },
    badgeRow: {
        display: 'flex',
        gap: '12px'
    },
    roleBadge: {
        background: 'rgba(255,255,255,0.2)',
        padding: '5px 14px',
        borderRadius: '20px',
        color: 'white',
        fontSize: '0.85rem',
        backdropFilter: 'blur(10px)',
        textTransform: 'capitalize',
        border: '1px solid rgba(255,255,255,0.3)',
        fontWeight: '500'
    },
    levelBadge: {
        background: 'rgba(255,255,255,0.9)',
        padding: '5px 14px',
        borderRadius: '20px',
        color: 'var(--primary)',
        fontSize: '0.85rem',
        border: '1px solid rgba(255,255,255,1)',
        fontWeight: '600'
    },
    mainContainer: {
        marginTop: '-30px',
        position: 'relative',
        zIndex: 10
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '20px',
        marginBottom: '30px'
    },
    statLabel: {
        color: '#64748b',
        fontSize: '0.85rem',
        marginBottom: '4px',
        fontWeight: '600'
    },
    statValue: {
        fontSize: '1.8rem',
        fontWeight: 'bold',
        color: '#1e293b'
    },
    contentLayout: {
        display: 'grid',
        gridTemplateColumns: '280px 1fr',
        gap: '30px',
        '@media (max-width: 768px)': {
            gridTemplateColumns: '1fr'
        }
    },
    sidebar: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px'
    },
    tabButton: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        background: 'transparent',
        border: 'none',
        borderRadius: '8px',
        color: '#64748b',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'all 0.2s ease',
        fontWeight: '600'
    },
    tabButtonActive: {
        background: 'rgba(39, 176, 91, 0.1)',
        color: 'var(--primary)',
    },
    contentArea: {
        minHeight: '500px'
    },
    formSection: {
        padding: '10px'
    },
    sectionTitle: {
        color: '#1e293b',
        fontSize: '1.5rem',
        marginBottom: '25px',
        fontWeight: '700'
    },
    formGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px',
        marginBottom: '30px'
    },
    inputGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
    },
    inputGroupFull: {
        gridColumn: '1 / -1',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
    },
    label: {
        color: '#64748b',
        fontSize: '0.9rem',
        fontWeight: '600'
    },
    inputWrapper: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center'
    },
    inputIcon: {
        position: 'absolute',
        left: '14px',
        color: '#94a3b8'
    },
    input: {
        width: '100%',
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        padding: '12px 12px 12px 42px',
        color: '#1e293b',
        outline: 'none',
        transition: 'all 0.2s ease',
        fontWeight: '500'
    },
    textarea: {
        width: '100%',
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        padding: '12px',
        color: '#1e293b',
        outline: 'none',
        resize: 'none',
        fontFamily: 'inherit',
        fontWeight: '500'
    },
    saveButton: {
        background: 'var(--primary)',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        padding: '14px 28px',
        fontWeight: '700',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: '0 4px 12px rgba(39, 176, 91, 0.3)'
    },
    secondaryButton: {
        background: '#ffffff',
        color: '#1e293b',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        padding: '12px 24px',
        fontWeight: '600',
        cursor: 'pointer',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
    },
    dangerButton: {
        background: 'rgba(239, 68, 68, 0.1)',
        color: '#ef4444',
        border: '1px solid rgba(239, 68, 68, 0.3)',
        borderRadius: '10px',
        padding: '10px 20px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
    },
    activityItem: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '15px 0',
        alignItems: 'center'
    },
    activityInfo: {
        display: 'flex',
        gap: '15px',
        alignItems: 'center'
    },
    activityIcon: {
        width: '44px',
        height: '44px',
        background: '#f1f5f9',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.3rem'
    },
    alert: {
        padding: '12px 20px',
        borderRadius: '10px',
        marginBottom: '20px',
        fontSize: '0.9rem',
        fontWeight: '500'
    }
};

export default Settings;
